import emailjs from '@emailjs/browser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SendIcon from '@mui/icons-material/Send';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAppState } from '../../../Context/AppStateContext';
import { theme } from '../../../theme';
import { HackerButton } from '../../../theme/HackerComponents';
import BorderBox from '../../ui/BorderBox.component';
import Modal from '../../ui/Modal.component';
import MotionContainer from '../../ui/MotionContainer.component';

interface ContactProps {
  visible: boolean;
  onClose: () => void;
  onFocus?: () => void;
  isActive?: boolean;
}

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 10px;
  margin-bottom: 30px;
`;

const InfoCard = styled.div`
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;

  svg {
    font-size: 1.5rem;
    color: ${theme.colors.green};
    margin-right: 15px;
  }

  .info {
    h4 {
      color: white;
      margin: 0 0 5px 0;
      font-size: 14px;
      font-weight: 600;
    }

    p {
      color: rgba(255, 255, 255, 0.8);
      margin: 0;
      font-size: 13px;
    }

    a {
      color: ${theme.colors.green};
      text-decoration: none;
      font-family: 'Courier New', monospace;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const FormSection = styled.div`
  padding: 0px;
`;

const FormTitle = styled.h3`
  color: ${theme.colors.green};
  margin: 0 0 20px 0;
  font-family: 'Courier New', monospace;
  font-size: 16px;
  display: flex;
  align-items: center;

  svg {
    margin-right: 10px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  color: rgba(208, 208, 208, 0.9);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid ${theme.colors.green}40;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  font-family: 'Courier New', monospace;

  &:focus {
    outline: none;
    border-color: ${theme.colors.green};
    box-shadow: 0 0 0 2px ${theme.colors.green}30;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid ${theme.colors.green}40;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  min-height: 120px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${theme.colors.green};
    box-shadow: 0 0 0 2px ${theme.colors.green}30;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid ${theme.colors.green}40;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  font-family: 'Courier New', monospace;

  &:focus {
    outline: none;
    border-color: ${theme.colors.green};
    box-shadow: 0 0 0 2px ${theme.colors.green}30;
  }

  option {
    background: rgba(0, 20, 0, 0.9);
    color: white;
  }
`;

const SubmitButton = styled.button`
  background: ${theme.colors.green};
  color: black;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  svg {
    margin-left: 8px;
  }

  &:hover {
    background: ${theme.colors.green}cc;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(31, 207, 94, 0.1);
  border: 1px solid ${theme.colors.green}60;
  border-radius: 4px;
  padding: 15px;
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 14px;
  margin-bottom: 20px;
  text-align: center;
`;

const ConfirmationContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px 20px;
`;

const ConfirmationTitle = styled.h2`
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 24px;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ConfirmationText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Courier New', monospace;
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 30px 0;
`;

const CountdownContainer = styled.div`
  width: 100%;
  max-width: 300px;
`;

const CountdownText = styled.div`
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 14px;
  margin-bottom: 10px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: ${theme.colors.green};
  width: ${props => props.progress}%;
  transition: width 0.1s ease-out;
  border-radius: 2px;
`;

const ErrorMessage = styled.div`
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.6);
  border-radius: 4px;
  padding: 15px;
  color: #ff6b6b;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  margin-bottom: 20px;
  text-align: center;
`;

const Contact: React.FC<ContactProps> = ({ visible, onClose, onFocus, isActive }) => {
  const { t } = useTranslation();
  const { isOnMobile } = useAppState();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  // Cleanup countdown when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (countdown > 0) {
        setCountdown(0);
        setIsClosing(false);
      }
    };
  }, [visible]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing again
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // EmailJS configuration - using environment variables for better security
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_fd3bcqo';
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_yric0rc';
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'Zu7e25j6Vy3b0nIvT';

      // Prepare template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_email: 'post@ariangarshi.no',
      };

      // Send email using EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: 'general', message: '' });
      setCountdown(8);
      setIsClosing(true);

      // Start countdown timer
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            onClose(); // Close the modal
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Failed to send email:', err);
      setError(
        'Failed to send message. Please try again or contact me directly at post@ariangarshi.no'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <Modal
      color={theme.colors.green}
      header={t('contact.header', 'CONTACT')}
      widthString={isOnMobile ? '95%' : '800px'}
      xPositionPercent={isOnMobile ? 50 : 50}
      yPositionPercent={15}
      onClose={onClose}
      onFocus={onFocus}
      isActive={isActive}
      icon={<EmailIcon />}
    >
      <MotionContainer>
        <BorderBox isActive={true} noBackground={true}>
          <ContactInfo>
            <InfoCard>
              <EmailIcon />
              <div className="info">
                <h4>{t('contact.social.email.label')}</h4>
                <p>
                  <a href={t('contact.social.email.url')}>{t('contact.info.email')}</a>
                </p>
              </div>
            </InfoCard>
            <InfoCard>
              <LinkedInIcon />
              <div className="info">
                <h4>{t('contact.social.linkedin.label')}</h4>
                <p>
                  <a
                    href={t('contact.social.linkedin.url')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('contact.social.linkedin.username')}
                  </a>
                </p>
              </div>
            </InfoCard>

            <InfoCard>
              <GitHubIcon />
              <div className="info">
                <h4>{t('contact.social.github.label')}</h4>
                <p>
                  <a
                    href={t('contact.social.github.url')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('contact.social.github.username')}
                  </a>
                </p>
              </div>
            </InfoCard>
          </ContactInfo>

          {isClosing ? (
            <ConfirmationContainer>
              <ConfirmationTitle>
                <CheckCircleIcon />
                {t('contact.confirmation.title', 'Message Sent Successfully!')}
              </ConfirmationTitle>
              <ConfirmationText>
                {t(
                  'contact.confirmation.message',
                  "Thank you for reaching out! Your message has been delivered to post@ariangarshi.no. I'll get back to you as soon as possible."
                )}
              </ConfirmationText>
              <CountdownContainer>
                <CountdownText>
                  {t('contact.confirmation.countdown', 'Closing in {{count}} second{{plural}}...', {
                    count: countdown,
                    plural: countdown !== 1 ? 's' : '',
                  })}
                </CountdownText>
                <ProgressBar>
                  <ProgressFill progress={(countdown / 8) * 100} />
                </ProgressBar>
              </CountdownContainer>
            </ConfirmationContainer>
          ) : (
            <FormSection>
              <FormTitle>
                <SendIcon />
                {t('contact.form.title')}
              </FormTitle>

              {isSubmitted && <SuccessMessage>{t('contact.form.success')}</SuccessMessage>}
              {error && <ErrorMessage>{error}</ErrorMessage>}

              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="name">{t('contact.form.fields.name')} *</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('contact.form.placeholders.name')}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">{t('contact.form.fields.email')} *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('contact.form.placeholders.email')}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="subject">{t('contact.form.fields.subject')}</Label>
                  <Select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                  >
                    <option value="general">{t('contact.form.subjects.general')}</option>
                    <option value="project">{t('contact.form.subjects.project')}</option>
                    <option value="hosting">{t('contact.form.subjects.hosting')}</option>
                    <option value="development">{t('contact.form.subjects.development')}</option>
                    <option value="design">{t('contact.form.subjects.design')}</option>
                    <option value="collaboration">
                      {t('contact.form.subjects.collaboration')}
                    </option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="message">{t('contact.form.fields.message')} *</Label>
                  <TextArea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={t('contact.form.placeholders.message')}
                    required
                  />
                </FormGroup>

                <HackerButton type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? t('contact.form.buttons.sending')
                    : t('contact.form.buttons.submit')}
                  <SendIcon />
                </HackerButton>
              </form>
            </FormSection>
          )}
        </BorderBox>
      </MotionContainer>
    </Modal>
  );
};

export default Contact;
